import {
  Alert,
  Badge,
  Button,
  Card,
  type Column,
  DataTable,
  Modal,
  PageHeader,
  SelectField,
  TextField,
} from '@safepos/ui';
import { PERMISSIONS } from '@safepos/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';

interface Outlet {
  id: string;
  name: string;
  code: string | null;
  mode: 'fnb' | 'retail';
  address: string | null;
  phone: string | null;
  timezone: string;
}
interface Role {
  id: string;
  name: string;
  isSystem: boolean;
  permissions: string[];
  employeeCount: number;
}
interface Employee {
  id: string;
  name: string;
  phone: string | null;
  active: boolean;
  roleId: string;
  roleName: string;
  outletIds: string[];
  hasPin: boolean;
  hasLogin: boolean;
  pinLockedUntil: string | null;
}
interface Device {
  id: string;
  name: string;
  type: 'pos' | 'kds';
  outletId: string;
  outletName: string | null;
  lastSeenAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

function useList<T>(key: string, path: string) {
  return useQuery({ queryKey: [key], queryFn: () => api.get<T>(path) });
}
function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: string[]) => keys.forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
}

// ---------------- Outlet ----------------
export function OutletsPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const { data = [], isLoading, error } = useList<Outlet[]>('outlets', '/outlets');
  const [editing, setEditing] = useState<Partial<Outlet> | null>(null);
  const save = useSubmit(async () => {
    const { id, ...body } = editing!;
    const payload = {
      name: body.name,
      mode: body.mode ?? 'fnb',
      code: body.code || undefined,
      address: body.address || undefined,
      phone: body.phone || undefined,
    };
    if (id) await api.patch(`/outlets/${id}`, payload);
    else await api.post('/outlets', payload);
    inv('outlets', 'subscription');
    setEditing(null);
  });
  const remove = useSubmit(async (id: string) => {
    if (!confirm('Hapus outlet ini?')) return;
    await api.del(`/outlets/${id}`);
    inv('outlets', 'subscription');
  });
  const cols: Column<Outlet>[] = [
    { header: 'Nama', cell: (o) => <span className="font-medium">{o.name}</span> },
    { header: 'Kode', cell: (o) => o.code ?? '—' },
    {
      header: 'Mode',
      cell: (o) => <Badge tone="info">{o.mode === 'fnb' ? 'F&B' : 'Retail'}</Badge>,
    },
    { header: 'Alamat', cell: (o) => o.address ?? '—' },
    {
      header: '',
      className: 'text-right',
      cell: (o) =>
        can('outlets:write') && (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditing(o)}>
              Ubah
            </Button>
            <Button variant="ghost" onClick={() => void remove.run(o.id)}>
              Hapus
            </Button>
          </div>
        ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Outlet"
        subtitle="Cabang/toko milik bisnis Anda"
        actions={
          can('outlets:write') && (
            <Button onClick={() => setEditing({ mode: 'fnb' })}>+ Tambah outlet</Button>
          )
        }
      />
      {(error || remove.error) && <Alert>{remove.error ?? (error as Error).message}</Alert>}
      <Card>{isLoading ? 'Memuat…' : <DataTable columns={cols} rows={data} />}</Card>
      <Modal
        open={!!editing}
        title={editing?.id ? 'Ubah outlet' : 'Tambah outlet'}
        onClose={() => setEditing(null)}
      >
        {editing && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            <TextField
              label="Nama outlet"
              value={editing.name ?? ''}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              required
            />
            <TextField
              label="Kode (opsional)"
              hint="2–10 huruf/angka, unik"
              value={editing.code ?? ''}
              onChange={(e) => setEditing({ ...editing, code: e.target.value })}
            />
            <SelectField
              label="Mode"
              value={editing.mode}
              onChange={(e) => setEditing({ ...editing, mode: e.target.value as Outlet['mode'] })}
            >
              <option value="fnb">F&B (meja, dapur)</option>
              <option value="retail">Retail (barcode, stok)</option>
            </SelectField>
            <TextField
              label="Alamat"
              value={editing.address ?? ''}
              onChange={(e) => setEditing({ ...editing, address: e.target.value })}
            />
            <TextField
              label="Telepon"
              value={editing.phone ?? ''}
              onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

// ---------------- Pegawai ----------------
export function EmployeesPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const employees = useList<Employee[]>('employees', '/employees');
  const roles = useList<Role[]>('roles', '/roles');
  const outlets = useList<Outlet[]>('outlets', '/outlets');
  const [form, setForm] = useState<{
    id?: string;
    name: string;
    phone: string;
    roleId: string;
    outletIds: string[];
    pin: string;
    email: string;
    password: string;
  } | null>(null);
  const [pinFor, setPinFor] = useState<Employee | null>(null);
  const [pin, setPin] = useState('');

  const save = useSubmit(async () => {
    const f = form!;
    if (f.id) {
      await api.patch(`/employees/${f.id}`, {
        name: f.name,
        phone: f.phone || null,
        roleId: f.roleId,
        outletIds: f.outletIds,
      });
    } else {
      await api.post('/employees', {
        name: f.name,
        phone: f.phone || undefined,
        roleId: f.roleId,
        outletIds: f.outletIds,
        pin: f.pin || undefined,
        email: f.email || undefined,
        password: f.password || undefined,
      });
    }
    inv('employees', 'subscription');
    setForm(null);
  });
  const toggle = useSubmit(async (e: Employee) => {
    await api.patch(`/employees/${e.id}`, { active: !e.active });
    inv('employees', 'subscription');
  });
  const remove = useSubmit(async (e: Employee) => {
    if (!confirm(`Hapus pegawai ${e.name}?`)) return;
    await api.del(`/employees/${e.id}`);
    inv('employees', 'subscription');
  });
  const resetPin = useSubmit(async () => {
    await api.post(`/employees/${pinFor!.id}/reset-pin`, { pin });
    inv('employees');
    setPinFor(null);
    setPin('');
  });

  const outletName = (id: string) => outlets.data?.find((o) => o.id === id)?.name ?? '?';
  const cols: Column<Employee>[] = [
    { header: 'Nama', cell: (e) => <span className="font-medium">{e.name}</span> },
    { header: 'Peran', cell: (e) => <Badge>{e.roleName}</Badge> },
    { header: 'Outlet', cell: (e) => e.outletIds.map(outletName).join(', ') },
    {
      header: 'PIN POS',
      cell: (e) =>
        e.pinLockedUntil && new Date(e.pinLockedUntil) > new Date() ? (
          <Badge tone="danger">Terkunci</Badge>
        ) : e.hasPin ? (
          <Badge tone="success">Terpasang</Badge>
        ) : (
          <Badge tone="warning">Belum</Badge>
        ),
    },
    { header: 'Login Back Office', cell: (e) => (e.hasLogin ? 'Ya' : '—') },
    {
      header: 'Status',
      cell: (e) => (
        <Badge tone={e.active ? 'success' : 'neutral'}>{e.active ? 'Aktif' : 'Nonaktif'}</Badge>
      ),
    },
    {
      header: '',
      className: 'text-right',
      cell: (e) =>
        can('employees:write') && (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                setForm({
                  id: e.id,
                  name: e.name,
                  phone: e.phone ?? '',
                  roleId: e.roleId,
                  outletIds: e.outletIds,
                  pin: '',
                  email: '',
                  password: '',
                })
              }
            >
              Ubah
            </Button>
            <Button variant="secondary" onClick={() => setPinFor(e)}>
              Reset PIN
            </Button>
            <Button variant="ghost" onClick={() => void toggle.run(e)}>
              {e.active ? 'Nonaktifkan' : 'Aktifkan'}
            </Button>
            <Button variant="ghost" onClick={() => void remove.run(e)}>
              Hapus
            </Button>
          </div>
        ),
    },
  ];
  const err = toggle.error ?? remove.error;
  return (
    <>
      <PageHeader
        title="Pegawai"
        subtitle="Kelola pegawai, peran, dan PIN login POS"
        actions={
          can('employees:write') && (
            <Button
              onClick={() =>
                setForm({
                  name: '',
                  phone: '',
                  roleId: roles.data?.find((r) => r.name === 'Kasir')?.id ?? '',
                  outletIds: outlets.data?.slice(0, 1).map((o) => o.id) ?? [],
                  pin: '',
                  email: '',
                  password: '',
                })
              }
            >
              + Tambah pegawai
            </Button>
          )
        }
      />
      {err && <Alert>{err}</Alert>}
      <Card>
        {employees.isLoading ? 'Memuat…' : <DataTable columns={cols} rows={employees.data ?? []} />}
      </Card>

      <Modal
        open={!!form}
        title={form?.id ? 'Ubah pegawai' : 'Tambah pegawai'}
        onClose={() => setForm(null)}
      >
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            <TextField
              label="Nama"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <TextField
              label="Telepon"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <SelectField
              label="Peran"
              value={form.roleId}
              onChange={(e) => setForm({ ...form, roleId: e.target.value })}
              required
            >
              <option value="" disabled>
                Pilih peran
              </option>
              {roles.data?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </SelectField>
            <fieldset>
              <legend className="mb-1 text-sm font-medium text-slate-700">Outlet</legend>
              <div className="space-y-1">
                {outlets.data?.map((o) => (
                  <label key={o.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.outletIds.includes(o.id)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          outletIds: e.target.checked
                            ? [...form.outletIds, o.id]
                            : form.outletIds.filter((x) => x !== o.id),
                        })
                      }
                    />
                    {o.name}
                  </label>
                ))}
              </div>
            </fieldset>
            {!form.id && (
              <>
                <TextField
                  label="PIN POS (4–6 digit)"
                  inputMode="numeric"
                  maxLength={6}
                  value={form.pin}
                  onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '') })}
                />
                <p className="text-xs text-slate-500">
                  Opsional: isi email & password bila pegawai juga perlu masuk ke Back Office.
                </p>
                <TextField
                  label="Email (opsional)"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <TextField
                  label="Password (opsional)"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={save.busy || form.outletIds.length === 0}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={!!pinFor}
        title={`Reset PIN · ${pinFor?.name ?? ''}`}
        onClose={() => setPinFor(null)}
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void resetPin.run();
          }}
        >
          {resetPin.error && <Alert>{resetPin.error}</Alert>}
          <TextField
            label="PIN baru (4–6 digit)"
            inputMode="numeric"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            autoFocus
            required
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setPinFor(null)}>
              Batal
            </Button>
            <Button type="submit" disabled={resetPin.busy || pin.length < 4}>
              Simpan PIN
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// ---------------- Peran ----------------
export function RolesPage() {
  const { can, me } = useAuth();
  const inv = useInvalidate();
  const roles = useList<Role[]>('roles', '/roles');
  const [form, setForm] = useState<{ id?: string; name: string; permissions: string[] } | null>(
    null,
  );
  const mine = me?.permissions ?? [];
  const grantable = (p: string) => mine.includes('*') || mine.includes(p);
  const save = useSubmit(async () => {
    if (form!.id)
      await api.patch(`/roles/${form!.id}`, { name: form!.name, permissions: form!.permissions });
    else await api.post('/roles', { name: form!.name, permissions: form!.permissions });
    inv('roles');
    setForm(null);
  });
  const remove = useSubmit(async (r: Role) => {
    if (!confirm(`Hapus peran ${r.name}?`)) return;
    await api.del(`/roles/${r.id}`);
    inv('roles');
  });
  const cols: Column<Role>[] = [
    { header: 'Peran', cell: (r) => <span className="font-medium">{r.name}</span> },
    {
      header: 'Jenis',
      cell: (r) => (
        <Badge tone={r.isSystem ? 'info' : 'neutral'}>{r.isSystem ? 'Bawaan' : 'Kustom'}</Badge>
      ),
    },
    {
      header: 'Izin',
      cell: (r) => (r.permissions.includes('*') ? 'Semua izin' : `${r.permissions.length} izin`),
    },
    { header: 'Pegawai', cell: (r) => r.employeeCount },
    {
      header: '',
      className: 'text-right',
      cell: (r) =>
        can('roles:write') &&
        !r.isSystem && (
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setForm({ id: r.id, name: r.name, permissions: r.permissions })}
            >
              Ubah
            </Button>
            <Button variant="ghost" onClick={() => void remove.run(r)}>
              Hapus
            </Button>
          </div>
        ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Peran & Izin"
        subtitle="Peran bawaan tidak dapat diubah; buat peran kustom sesuai kebutuhan"
        actions={
          can('roles:write') && (
            <Button onClick={() => setForm({ name: '', permissions: [] })}>+ Peran baru</Button>
          )
        }
      />
      {remove.error && <Alert>{remove.error}</Alert>}
      <Card>
        {roles.isLoading ? 'Memuat…' : <DataTable columns={cols} rows={roles.data ?? []} />}
      </Card>
      <Modal
        open={!!form}
        title={form?.id ? 'Ubah peran' : 'Peran baru'}
        onClose={() => setForm(null)}
      >
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            <TextField
              label="Nama peran"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <fieldset>
              <legend className="mb-1 text-sm font-medium text-slate-700">
                Izin (hanya izin yang Anda miliki yang bisa diberikan)
              </legend>
              <div className="grid max-h-64 grid-cols-1 gap-1 overflow-auto sm:grid-cols-2">
                {PERMISSIONS.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      disabled={!grantable(p)}
                      checked={form.permissions.includes(p)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          permissions: e.target.checked
                            ? [...form.permissions, p]
                            : form.permissions.filter((x) => x !== p),
                        })
                      }
                    />
                    <code className="text-xs">{p}</code>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

// ---------------- Perangkat ----------------
export function DevicesPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const devices = useList<Device[]>('devices', '/devices');
  const outlets = useList<Outlet[]>('outlets', '/outlets');
  const [form, setForm] = useState<{ outletId: string; type: 'pos' | 'kds' } | null>(null);
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const create = useSubmit(async () => {
    const r = await api.post<{ code: string; expiresAt: string }>(
      '/devices/activation-codes',
      form,
    );
    setCode(r);
    setForm(null);
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/devices/${id}`),
    onSuccess: () => inv('devices', 'subscription'),
  });
  const cols: Column<Device>[] = [
    { header: 'Perangkat', cell: (d) => <span className="font-medium">{d.name}</span> },
    {
      header: 'Jenis',
      cell: (d) => <Badge tone="info">{d.type === 'pos' ? 'Kasir' : 'KDS'}</Badge>,
    },
    { header: 'Outlet', cell: (d) => d.outletName ?? '—' },
    { header: 'Terakhir aktif', cell: (d) => fmt(d.lastSeenAt) },
    {
      header: 'Status',
      cell: (d) =>
        d.revokedAt ? <Badge tone="danger">Dicabut</Badge> : <Badge tone="success">Aktif</Badge>,
    },
    {
      header: '',
      className: 'text-right',
      cell: (d) =>
        can('devices:write') &&
        !d.revokedAt && (
          <Button
            variant="ghost"
            onClick={() => confirm(`Cabut perangkat ${d.name}?`) && revoke.mutate(d.id)}
          >
            Cabut
          </Button>
        ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Perangkat"
        subtitle="Tablet/PC kasir dan layar dapur yang terdaftar"
        actions={
          can('devices:write') && (
            <Button onClick={() => setForm({ outletId: outlets.data?.[0]?.id ?? '', type: 'pos' })}>
              + Aktivasi perangkat
            </Button>
          )
        }
      />
      {revoke.error && <Alert>{(revoke.error as Error).message}</Alert>}
      <Card>
        {devices.isLoading ? 'Memuat…' : <DataTable columns={cols} rows={devices.data ?? []} />}
      </Card>

      <Modal open={!!form} title="Buat kode aktivasi" onClose={() => setForm(null)}>
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void create.run();
            }}
          >
            {create.error && <Alert>{create.error}</Alert>}
            <SelectField
              label="Outlet"
              value={form.outletId}
              onChange={(e) => setForm({ ...form, outletId: e.target.value })}
            >
              {outlets.data?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Jenis perangkat"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as 'pos' | 'kds' })}
            >
              <option value="pos">Kasir (POS)</option>
              <option value="kds">Layar dapur (KDS)</option>
            </SelectField>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={create.busy}>
                Buat kode
              </Button>
            </div>
          </form>
        )}
      </Modal>
      <Modal
        open={!!code}
        title="Kode aktivasi"
        onClose={() => {
          setCode(null);
          inv('devices');
        }}
      >
        {code && (
          <div className="space-y-4 text-center">
            <p className="text-sm text-slate-600">
              Masukkan kode ini di aplikasi POS pada perangkat baru. Berlaku sekali pakai sampai{' '}
              {fmt(code.expiresAt)}.
            </p>
            <p className="select-all rounded-xl bg-slate-100 py-4 font-mono text-3xl tracking-widest">
              {code.code}
            </p>
            <Button
              onClick={() => {
                setCode(null);
                inv('devices');
              }}
            >
              Selesai
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
}
